import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import { TrackerService } from '../services/tracker.service';
import { MonitorService } from '../../../core/services/core.monitor.service';
import { TrackerFilterService } from '../services/tracker.filter.service';
import { TrackerQueryService } from '../services/tracker.query.service';
import { TrackerTicklerService } from '../../../widgets/ticklerBar/tracker.tickler.service';
import { PollService } from '../../../core/services/core.poll.service';
import { UiService } from '../../../core/services/core.ui.service';
import { UserService } from '../../../core/services/core.user.service';
import { TourService } from '../../../core/modules/tour/tour.service';
import { TrackeritemRest } from '../services/trackeritem.rest';
import { NotificationService } from '../../../core/services/core.notification.service';
import { StateService } from '@uirouter/angular';
import { GfTitleService } from '../../../core/services/core.title.service';
import { SiteConfigService } from '../../../core/services/core.site-config.service';
import { buildTrackerMacroContext, expandTrackerMacros } from '../services/tracker.macro.functions';
import * as _ from 'lodash';

@Component({
  selector: 'trackeritem-browse-screen',
  template: require('./trackeritem.browse.screen.component.html')
})
export class TrackerItemBrowseScreenComponent implements OnInit, OnDestroy {
  @Input() project: any;
  @Input() tracker: any;
  @Input() queries: any;
  @Input() queryId: any;
  @Input() projectMenu: any;

  query: any = null;
  view: string;
  trackerItems: any = null;
  projectTrackers: any = [];
  acls: any;
  isSiteAdmin: boolean;
  monitoring: boolean;
  ticklers: any = { queryTicklers: [], ticklerAddQueries: [] };
  tibText: any;
  translations: any;
  showFilters: boolean = false;
  filters: any[] = [];
  sortFields: any[] = [];
  tibOptions: any = {};
  lastValueForRefresh: any;
  refreshNeeded: boolean = false;
  updateSubmitted: boolean = false;

  constructor(
    protected stateService: StateService,
    private titleService: GfTitleService,
    private trackerService: TrackerService,
    private monitorService: MonitorService,
    private trackerFilterService: TrackerFilterService,
    private trackerQueryService: TrackerQueryService,
    private trackerTicklerService: TrackerTicklerService,
    private pollService: PollService,
    private uiService: UiService,
    private userService: UserService,
    private tourService: TourService,
    private trackeritemRest: TrackeritemRest,
    private notificationService: NotificationService,
    private location: Location,
    private siteConfigService: SiteConfigService
  ) {}

  /**
   * Expands the project-scoped macros an admin may have used in the tracker's
   * instruction text. Item-scoped macros (%URL%, %FIELDS_CHANGED%, ...) are
   * left as-is: no single tracker item is in scope on this screen.
   */
  private expandInstructions(text: string): string {
    const siteName = _.get(this.siteConfigService.getSiteConfig(), 'siteName', '');
    const context = buildTrackerMacroContext(
      this.tracker,
      siteName,
      (state: string, params: any) => this.stateService.href(state, params, { inherit: false }),
    );
    return expandTrackerMacros(text, context);
  }


  ngOnInit() {
    this.view = this.stateService.params.view || '';
    this.projectTrackers = _.get(this.project, 'rel.permissions.trackers', []);
    this.acls = this.trackerService.acl.get(this.projectTrackers, this.tracker.id);
    this.isSiteAdmin = this.userService.session.isSiteAdmin();
    this.monitoring = this.getMonitoringStatus();

    this.ticklers.ticklerAddQueries = this.trackerTicklerService.getTicklerAddQueries(this.tracker, this.queries, this.ticklers);

    if (this.tracker?.browseInstructions ?? null) {
      this.tibText = {
        type: 'markdown',
        title: this.tracker.trackerName,
        glyph: 'glyphicon-info-sign',
        content: this.expandInstructions(this.tracker.browseInstructions)
      };
    }

    this.translations = {
      filter_criteria: 'Filter Criteria',
      queries: 'Queries',
      new_in: 'New in'
    };

    if (!this.isMobileViewport() || this.isCustomQuery(_.get(this.query, 'id', ''))) {
      this.showFilters = false;
    };

    this.trackerTicklerService.refreshPolling('tracker', this.tracker.id);
    this.pollService.subscribe('tib', this.pollDataCallback.bind(this));

    this.notificationService.subscribe('tib', 'TRACKERITEM.MASS.DELETE.SUCCESS', (args) => {
      const bgJobId = args.jobId;
      if (bgJobId) {
        setTimeout(() => {
          this.trackerItems = null;
          this.pollService.add('bgjob', 'backgroundjob', bgJobId, 0);
        });
      } else {
        this.uiService.message.showSuccess('Tracker item successfully deleted');
        this.refresh();
      }
    });

    this.notificationService.subscribe('tib', 'TRACKERITEM.MASS.DELETE.FAILURE', () => {
      this.uiService.message.showSuccess('Tracker item delete failed');
    });

    this.init();
  }

  ngOnDestroy() {
    this.pollService.unsubscribe('tib');
    this.notificationService.unsubscribe('tib');
  }

  setView(viewName: string) {
    this.view = viewName;
    const params: any = {
      view: viewName
    };

    if (viewName === 'kanban') {
      if (!this.stateService.params.query || this.stateService.params.query === '') {
        params.query = 'custom_' + this.tracker.id;
      }
      params.highlight = null;
    }

    this.stateService.go('.', params, {notify: true, reload: false}).then(
      () => {
        this.setTour();
      },
      () => {}
    );
  }

  setTour() {
    if (this.view === 'kanban') {
      this.tourService.clearTour();
    } else if (this.isCustomQuery(_.get(this.query, 'id', ''))) {
      this.tourService.setTour('trackeritemBrowseFilter');
    } else {
      this.tourService.setTour('trackeritemBrowseCtrl');
    }
  }

  getMonitoringStatus(): boolean {
    const monitoring = _.find(_.get(this.tracker, 'rel.monitoring', []), (monitor) => {
      return monitor.id === this.userService.session.getUserId();
    });
    return !_.isEmpty(monitoring);
  }

  refresh() {
    this.runQuery();
  }

  toggleMonitor() {
    if (this.monitoring) {
      this.monitorService.unset('tracker', this.tracker.id).then(() => {
        this.monitoring = false;
      });
    } else {
      this.monitorService.set('tracker', this.tracker.id).then(() => {
        this.monitoring = true;
      });
    }
  }

  monitorButtonTitle() {
    return this.monitoring
      ? 'You are currently monitoring this tracker. Click to stop monitoring'
      : 'You are not monitoring this tracker. Click to start monitoring';
  }

  pollDataCallback(args) {
    const items = _.get(args, 'response.items', {});
    Object.values(items).forEach((item: any) => {
      switch (item.type) {
        case 'bgjob':
          if (item.items && item.items.length === 1) {
            const job = item.items[0];
            if (job.status === 3) {
              this.pollService.remove('bgjob', 'backgroundjob', item.refId);
              this.uiService.message.showSuccess(job.statusText, 'Delete request completed');
              this.uiService.message.showJobMessage(JSON.parse(job.responseText));
              this.runQuery();
            }
          } else {
            console.log('bgjob poll returned bad data:', item);
            this.pollService.remove('bgjob', 'backgroundjob', item.refId);
            this.runQuery();
          }
          break;

        case 'query':
          if (item.lastValue > this.lastValueForRefresh && !this.updateSubmitted) {
            this.lastValueForRefresh = item.lastValue;
            this.refreshNeeded = true;
          }
          this.updateSubmitted = false;
          break;

        case 'tickler':
          this.ticklers.queryTicklers = this.trackerTicklerService.addUrlToTicklers(
            this.trackerTicklerService.getDisplayTicklers(item.items)
          );
          this.updateAddQueryList();
          break;
      }
    });
  }

  addTickler(query) {
    this.trackerTicklerService.addTickler(query).then(
      () => {
        this.trackerTicklerService.refreshPolling('tracker', this.stateService.params.tracker);
        this.updateAddQueryList();
      },
      (result) => {
        console.log(result);
      }
    );
  }

  removeTickler(tickler) {
    this.trackerTicklerService.removeTickler(tickler).then(
      () => {
        this.ticklers.queryTicklers = this.trackerTicklerService.addUrlToTicklers(
          this.trackerTicklerService.getDisplayTicklers(_.without(this.ticklers.queryTicklers, tickler))
        );
        this.trackerTicklerService.refreshPolling('tracker', this.stateService.params.tracker);
        this.updateAddQueryList();
      },
      (result) => {
        console.log(result);
      }
    );
  }

  updateAddQueryList() {
    const queries = this.trackerTicklerService.getTicklerAddQueries(this.tracker, this.queries, this.ticklers);
    this.ticklers.ticklerAddQueries = Array.isArray(queries) && queries?.length > 0 ? queries : null;
  }

  runTicklerQuery(queryId) {
    const params = {
      query: queryId,
      page_num: 1
    };
    this.stateService.go('.', params, {notify: true, reload: false})
    .then(() => {
      this.runQuery();
    }, (result) => console.log(result));
  }

  setParam(param: string, value: any, refresh: boolean = false) {
    const params = {
      [param]: value
    };
    this.stateService.go('.', params, {notify: true, reload: false}).then(
      () => {
        if (refresh) {
          this.refresh();
        }
        this.setTour();
      },
      () => {}
    );
  }

  getQueryId() {
    const query = this.stateService.params.query || '';
    const defaultValue = 'custom_' + this.stateService.params.tracker;

    if (query) {
      return Promise.resolve(query);
    }

    return this.trackerQueryService.getCurrentTrackerQuery(this.stateService.params.tracker).then((ret: any) => {
      const value = ret?.preferenceValue || defaultValue;
      return value;
    }).catch(() => defaultValue);
  }

  resetFilters(params) {
    this.runQuery(params);
  }

  onUpdate(event: any) {
    this.runQuery();
  }

  private isMobileViewport(): boolean {
    return window.innerWidth <= 767;
  }

  runQuery(params: any = {}) {
    if (_.isEmpty(params)) {
      params = _.assign(this.stateService.params);
    }

    this.query = this.trackerQueryService.getQueryObject(this.queries, params.query) || _.get(this.queries, '[0].queries[0]', null);

    if (!this.query) return;

    // Hide quick query filters if not a custom query
    if (!this.isCustomQuery(_.get(this.query, 'id', ''))) {
      this.showFilters = false;
    }

    let promise;
    if (_.get(this.query, 'tracker.id', 0) !== this.tracker.id) {
      promise = this.trackerService.getTracker(this.stateService.params, [])
	  .then((tracker: any) => {
        if (this.tracker) {
          this.pollService.remove('tickler', 'tracker', this.tracker.id);
        }
        this.tracker = tracker;
        this.pollService.add('tickler', 'tracker', tracker.id);
      });
    } else {
      promise = Promise.resolve();
    }

    promise.then(() => {
      this.setWindowTitle();
      this.setTour();

      if (this.isCustomQuery(_.get(this.query, 'id', ''))) {
        this.filters = this.trackerFilterService.getFilters(this.tracker);
        // Merges filters parameter with other params for setting filter values
        const allParams = { ...params };
        const parsedFilters = this.extractFilters(params.filters);
        if (!_.isEmpty(parsedFilters)) {
          Object.assign(allParams, parsedFilters);
        }
        this.filters = this.trackerFilterService.setFilterValues(this.filters, allParams);
        this.showFilters = !this.isMobileViewport();
      }

      this.trackerItems = false;
      this.fetchTrackeritems(params).then((data) => {
        if (this.tracker?.id && this.query?.id) {
          this.trackerQueryService.saveCurrentTrackerQuery(this.tracker.id, this.query.id);
        }
        this.trackerItems = data;
      }).catch((err) => {
        if (err?.status === 404) {
          this.uiService.message.showError('Could not fetch tracker items, the tracker/query parameters were invalid.');
          const fallbackParams = {
            unixName: this.stateService.params.unixName,
            tracker: this.stateService.params.tracker,
            query: 'custom_' + this.tracker.id
          };
          this.stateService.go('.', fallbackParams, {notify: true, reload: false, inherit: false});
        } else {
          this.uiService.message.showError('There was an error fetching tracker items.');
        }
        this.trackerItems = [];
        console.log(err);
      });
    });
  }

  isCustomQuery(id: string): any {
    return !!id && id.toString().match(/(custom)/i);
  }

  init() {
    const currentParams = { ...this.stateService.params };
    if (!_.isEmpty(this.extractFilters(currentParams.filters))) {
      this.runQuery(currentParams);
      return;
    }

    const setStateAndRunQuery = (query) => {
      this.stateService.go('.', { query: query }, {notify: true, reload: false})
	  .then(() => {
        this.runQuery();
      });
    };

    if (this.trackerQueryService.isCustomQuery(this.queryId)) {
      const trackerId = this.trackerQueryService.getTrackerIdFromQueryId(this.queryId);
      this.trackerQueryService.getCustomQueryParams(trackerId).then((savedParams) => {
        const savedParamObject = _.isPlainObject(savedParams)
          ? (savedParams as Record<string, any>)
          : {};

        if (!_.isEmpty(savedParamObject)) {
          const params = {
            unixName: this.stateService.params.unixName,
            tracker: this.stateService.params.tracker,
            ...savedParamObject
          };
          this.stateService.go('.', params, {notify: true, reload: false, inherit: false})
          .then(() => {
            this.runQuery(params);
          });
        } else {
          setStateAndRunQuery(this.queryId);
        }
      }).catch(() => {
        setStateAndRunQuery(this.queryId);
      });
    } else {
      setStateAndRunQuery(this.queryId);
    }
  }

  setWindowTitle() {
    const queryName = this.isCustomQuery(this.stateService.params.query)
      ? 'Quick Filter'
      : this.query?.queryName || '';
    this.titleService.setTitle(this.project?.name, this.tracker?.trackerName, queryName);
  }

  setFallbackView() {
    this.stateService.go('home.project.summary.trackeritem', this.stateService.params);
  }

  toggleFilters() {
    this.showFilters = !this.showFilters;
  }

  fetchTrackeritems(params): Promise<any> {
    if (!params.query && !params.tracker) {
      return Promise.reject();
    }

    // Merges filters parameter into params
    const apiParams = { ...params };
    const parsedFilters = this.extractFilters(params.filters);
    if (!_.isEmpty(parsedFilters)) {
      Object.assign(apiParams, parsedFilters);
    }
    delete apiParams.filters;

    if (apiParams.query && apiParams.query.toString().match(/(custom)/i)) {
      delete apiParams.query;
    }

    // #65237 Iteration 3: bulk-loads open/closed child counts for the TIB row floodbar
    apiParams.rel = 'child_status';

    return this.trackeritemRest.fetch(0, apiParams).then((response: any) => {
      return _.has(response, 'items') && Array.isArray(response.items) ? response.items : [response];
    }).catch((err) => {
      return Promise.reject(err);
    });
  }

  private extractFilters(rawFilters: any): Record<string, any> {
    if (rawFilters == null) {
      return {};
    }

    if (_.isPlainObject(rawFilters)) {
      return rawFilters as Record<string, any>;
    }

    if (typeof rawFilters !== 'string') {
      return {};
    }

    let decoded = rawFilters;
    try {
      decoded = decodeURIComponent(rawFilters);
    } catch {
      decoded = rawFilters;
    }

    const trimmedCandidate = decoded.trim();
    if (!trimmedCandidate) {
      return {};
    }

    try {
      const parsed = JSON.parse(trimmedCandidate);
      return _.isPlainObject(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
}
